import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Logo } from "@/components/Brand";
import { SiteFooter } from "@/components/SiteFooter";

const CONTACT = "contact@club-altitude.com";
const Mail = () => (
  <a href={`mailto:${CONTACT}`} data-testid="privacy-contact-email-link" className="font-medium text-navy underline decoration-gold-deep underline-offset-2 hover:text-gold-deep">{CONTACT}</a>
);

const SECTIONS = [
  ["qui", "1. Qui sommes-nous ?", [
    "Altitude Club est un service d'alertes de vols en classe Business. Nous surveillons les tarifs et envoyons à nos membres, par e-mail, des offres au départ de leur aéroport (Paris-CDG au lancement).",
    "Altitude Club est un produit et service proposé par Vikora AI LLC, Limited Liability Company de droit américain, constituée dans l'État du Wyoming (États-Unis), dont le siège social est situé 1021 E Lincolnway, #10308, Cheyenne, WY 82001 — Laramie County, USA, immatriculée sous le numéro EIN (Employer Identification Number) 37-2236204. Vikora AI LLC est le responsable du traitement de vos données.",
    <>Pour toute question sur vos données, écrivez-nous à <Mail />.</>,
  ]],
  ["donnees", "2. Les données que nous collectons", [
    "Lors de l'inscription : votre pays et votre aéroport de départ, votre destination de rêve (facultative), votre civilité, votre nom, votre prénom et votre adresse e-mail.",
    "Pour l'abonnement : la formule choisie, les dates d'essai et d'abonnement. Les données de carte bancaire sont traitées directement par notre prestataire de paiement, Stripe : nous n'y avons pas accès.",
    "Lorsque vous nous contactez : le contenu de vos messages et, pour la garantie, les justificatifs que vous nous transmettez (confirmations de réservation, e-mails d'alerte).",
    "Données techniques : adresse IP, type de navigateur et d'appareil, nécessaires au bon fonctionnement et à la sécurité du site.",
  ]],
  ["usages", "3. Pourquoi nous les utilisons", [
    "Vous envoyer les offres Business au départ de votre aéroport (exécution du contrat).",
    "Gérer votre compte, votre essai gratuit, votre abonnement et sa résiliation (exécution du contrat).",
    "Traiter vos demandes, dont les demandes de remboursement au titre de la garantie (exécution du contrat).",
    "Sécuriser le site et prévenir la fraude (intérêt légitime).",
    "Respecter nos obligations légales, notamment comptables et fiscales (obligation légale).",
    "Vous envoyer des informations commerciales sur le club, uniquement si vous l'avez accepté. Vous pouvez vous désabonner à tout moment (consentement).",
  ]],
  ["partage", "4. Avec qui nous les partageons", [
    "Nous ne vendons jamais vos données.",
    "Elles sont transmises uniquement à nos prestataires, pour les besoins du service : hébergement du site (OVHcloud), envoi des e-mails depuis l'adresse contact@club-altitude.com et paiement (Stripe).",
    "Elles peuvent être communiquées aux autorités si la loi l'exige.",
    "Lorsque vous réservez un vol à partir d'une offre, vous le faites directement sur le site du vendeur : sa propre politique de confidentialité s'applique.",
  ]],
  ["duree", "5. Combien de temps nous les gardons", [
    "Tant que votre compte est actif, puis 3 ans après votre dernière activité pour vous recontacter, sauf opposition de votre part.",
    "Les justificatifs de paiement et factures sont conservés 10 ans, comme l'exige la loi.",
    "Les demandes liées à la garantie sont conservées le temps de leur traitement, puis jusqu'à 5 ans en cas de litige.",
    "Si vous supprimez votre compte, vos données sont effacées, sauf celles que la loi nous oblige à conserver.",
  ]],
  ["cookies", "6. Cookies et stockage local", [
    "Le site utilise uniquement le stockage de votre navigateur nécessaire à son fonctionnement (par exemple pour garder vos choix pendant l'inscription et votre session).",
    "Nous n'utilisons pas de cookies publicitaires ni de mesure d'audience. Si cela change, nous vous demanderons votre accord au préalable.",
  ]],
  ["securite", "7. Sécurité et transferts", [
    "Vos données sont protégées par des mesures techniques adaptées : connexion chiffrée (HTTPS), accès limité aux seules personnes qui en ont besoin.",
    "Vikora AI LLC étant établie aux États-Unis, vos données sont traitées aux États-Unis, de même que par certains de nos prestataires. Ces transferts hors de l'Union européenne sont encadrés par les clauses contractuelles types de la Commission européenne ou, le cas échéant, par le cadre de protection des données UE–États-Unis (Data Privacy Framework).",
  ]],
  ["droits", "8. Vos droits", [
    "Vous pouvez à tout moment accéder à vos données, les corriger, les supprimer, en demander une copie dans un format lisible, vous opposer à leur utilisation ou en demander la limitation.",
    "Vous pouvez retirer votre consentement aux e-mails commerciaux à tout moment, grâce au lien de désabonnement présent dans chaque e-mail.",
    "Vous pouvez aussi donner des instructions sur le sort de vos données après votre décès.",
    <>Pour exercer vos droits, écrivez-nous à <Mail />. Nous répondons dans un délai d'un mois.</>,
    <>Si vous estimez que vos droits ne sont pas respectés, vous pouvez saisir la CNIL (<a href="https://www.cnil.fr" target="_blank" rel="noreferrer" data-testid="privacy-cnil-link" className="underline underline-offset-2">www.cnil.fr</a>).</>,
  ]],
  ["mineurs", "9. Mineurs", [
    "Altitude Club s'adresse aux personnes majeures. Nous ne collectons pas sciemment de données concernant des mineurs.",
  ]],
  ["modifs", "10. Modifications", [
    "Nous pouvons mettre à jour cette politique. La date de dernière mise à jour figure en haut de cette page. En cas de changement important, nous vous en informerons par e-mail.",
  ]],
];

const Section = ({ id, title, items }) => (
  <section data-testid={`privacy-section-${id}`} className="border-t border-navy/10 pt-8">
    <h2 className="font-display text-xl text-navy sm:text-2xl">{title}</h2>
    <ul className="mt-4 space-y-3">
      {items.map((t, i) => (
        <li key={i} className="flex gap-3 text-sm leading-relaxed text-navy/75 sm:text-base">
          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gold-deep" />
          <span>{t}</span>
        </li>
      ))}
    </ul>
  </section>
);

export default function Privacy() {
  return (
    <div className="min-h-screen bg-ivory">
      <header className="flex justify-center px-5 pb-4 pt-8 sm:pt-12"><Logo tone="dark" /></header>
      <main className="mx-auto max-w-2xl px-5 pb-16 sm:px-6">
        <Link to="/" data-testid="privacy-back-link" className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy">
          <ArrowLeft className="h-4 w-4" /> Retour à l'accueil
        </Link>
        <p className="eyebrow mt-8 text-gold-deep">Dernière mise à jour : 2 novembre 2026</p>
        <h1 data-testid="privacy-title" className="mt-3 font-display text-4xl text-navy sm:text-5xl">Politique de confidentialité</h1>
        <p className="mt-5 text-base leading-relaxed text-navy/70">
          Cette page explique quelles données Altitude Club collecte, pourquoi, combien de temps nous les gardons et comment exercer vos droits, conformément au RGPD et à la loi Informatique et Libertés.
        </p>
        <div className="mt-10 space-y-8">
          {SECTIONS.map(([id, title, items]) => <Section key={id} id={id} title={title} items={items} />)}
        </div>
      </main>
      <SiteFooter tone="light" />
    </div>
  );
}
